/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { db } from '../core/storage/database'
import type { Compilation } from '../core/models/Play'
import { apiClient } from '../lib/api/client'
import type { MissingAudio } from './audioDownloadService'
import {
  storeAudioManifest,
  markCompilationPartiallyDownloaded,
  type AudioManifestLocal,
  type ManifestLine,
} from './audioValidationService'
import { generateId } from '../core/utils/id'

// Verrou en mémoire : évite les téléchargements ZIP concurrents pour un même compilationId
const _zipDownloadInProgress = new Map<string, Promise<void>>()

// Tracking temps de démarrage pour détection stalled downloads
const _zipDownloadStartTimes = new Map<string, number>()

// Configuration monitoring
const STALLED_DOWNLOAD_THRESHOLD_MS = 120000 // 2 minutes
const CLEANUP_INTERVAL_MS = 30000 // 30 secondes

// Interval de cleanup
let _cleanupIntervalId: ReturnType<typeof setInterval> | null = null

import type { VoiceAssignment } from '../core/models/Play'

// ============================================================================
// Types
// ============================================================================

export interface CompilationProgress {
  compilationId: string
  status: Compilation['status']
  progress: number // 0–100
  linesProcessed: number
  linesTotal: number | null
  cacheHitRate?: number | null
  currentStep?: string | null
  error?: string | null
}

export interface AudioManifest {
  lines: Array<{
    lineId: string
    hash: string
    url: string
    text: string
    voiceId: string
    duration: number
    size: number
    fromCache: boolean
  }>
  totalDuration: number
  totalSize: number
  cacheHitRate: number
}

// ============================================================================
// requestCompilation
// ============================================================================

/**
 * Crée une compilation sur le serveur avec le voice_mapping extrait des voiceAssignments.
 * Annule automatiquement toute compilation PENDING/PROCESSING précédente pour la même version.
 */
// Clé backend réservée pour la voix off — doit correspondre à VOIX_OFF_KEY dans compilation_service.py
const BACKEND_VOIX_OFF_KEY = 'VOIX_OFF'
// Clé frontend (characterId) de la voix off
const FRONTEND_VOIX_OFF_ID = '__voix_off__'

export async function requestCompilation(
  versionId: string,
  voiceAssignments: VoiceAssignment[]
): Promise<string> {
  // Construire le voice_mapping { characterName: voiceId }
  // La voix off utilise la clé backend "VOIX_OFF" (indépendamment du nom d'affichage "Voix Off")
  const voiceMapping: Record<string, string> = {}
  for (const va of voiceAssignments) {
    if (!va.voiceId) continue
    if (va.characterId === FRONTEND_VOIX_OFF_ID) {
      // Forcer la clé backend attendue par compilation_service.py
      voiceMapping[BACKEND_VOIX_OFF_KEY] = va.voiceId
    } else if (va.characterName) {
      voiceMapping[va.characterName] = va.voiceId
    }
  }

  console.warn(
    '[compilationService] requestCompilation',
    JSON.stringify(
      { versionId, voiceAssignmentsCount: voiceAssignments.length, voiceMapping },
      null,
      2
    )
  )

  const response = await apiClient.post<{ id: string; status: string; versionId: string }>(
    '/audio/compile',
    { version_id: versionId, voice_mapping: voiceMapping }
  )

  console.warn('[compilationService] requestCompilation → server response', {
    compilationId: response.id,
    status: response.status,
  })

  const compilationId = response.id

  // Annuler localement les compilations précédentes actives
  const previous = await db.compilations
    .where('textVersionId')
    .equals(versionId)
    .filter((c) => c.status === 'PENDING' || c.status === 'PROCESSING')
    .toArray()

  for (const prev of previous) {
    if (prev.id !== compilationId) {
      await db.compilations.update(prev.id, { status: 'CANCELLED' })
    }
  }

  // Vérifier si la compilation existe déjà localement (réponse idempotente)
  const existing = await db.compilations.get(compilationId)
  if (!existing) {
    await db.compilations.add({
      id: compilationId,
      textVersionId: versionId,
      userId: '',
      status: 'PENDING',
      progress: 0,
      audioFormat: 'opus',
      audioBitrate: 32,
      linesProcessed: 0,
      cacheHits: 0,
      cacheMisses: 0,
      createdAt: new Date().toISOString(),
      syncStatus: 'PENDING',
    })
  }

  return compilationId
}

// ============================================================================
// pollCompilationStatus
// ============================================================================

/**
 * Polling du statut d'une compilation avec interval de 5s.
 * Déclenche le téléchargement ZIP à la completion.
 * Retourne une fonction pour arrêter le polling.
 */
export function pollCompilationStatus(
  compilationId: string,
  onProgress: (progress: CompilationProgress) => void,
  onCompleted: (compilationId: string) => void,
  onFailed?: (error: string) => void
): () => void {
  const POLL_INTERVAL = 5000
  const MAX_POLL_TIME = 10 * 60 * 1000 // 10 minutes

  const startTime = Date.now()
  let stopped = false
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null

  const poll = async () => {
    if (stopped) return

    try {
      const compilation = await apiClient.get<{
        id: string
        status: string
        versionId: string
        progress: number
        linesProcessed: number
        linesTotal: number | null
        currentStep: string | null
        error: string | null
        cacheHitRate: number | null
        cacheHits: number
        cacheMisses: number
        totalDuration: number | null
        totalSize: number | null
        startedAt: string | null
        completedAt: string | null
        createdAt: string | null
      }>(`/audio/compile/${compilationId}`)

      if (stopped) return

      // Mettre à jour IndexedDB
      await db.compilations.update(compilationId, {
        status: compilation.status as Compilation['status'],
        progress: compilation.progress,
        linesProcessed: compilation.linesProcessed,
        linesTotal: compilation.linesTotal ?? undefined,
        currentStep: compilation.currentStep ?? undefined,
        error: compilation.error ?? undefined,
        cacheHitRate: compilation.cacheHitRate ?? undefined,
        cacheHits: compilation.cacheHits,
        cacheMisses: compilation.cacheMisses,
        totalDuration: compilation.totalDuration ?? undefined,
        totalSize: compilation.totalSize ?? undefined,
        startedAt: compilation.startedAt ?? undefined,
        completedAt: compilation.completedAt ?? undefined,
      })

      onProgress({
        compilationId,
        status: compilation.status as Compilation['status'],
        progress: compilation.progress,
        linesProcessed: compilation.linesProcessed,
        linesTotal: compilation.linesTotal ?? null,
        cacheHitRate: compilation.cacheHitRate,
        currentStep: compilation.currentStep,
        error: compilation.error,
      })

      if (compilation.status === 'COMPLETED') {
        stopped = true
        try {
          await downloadCompilationZip(compilationId)
        } catch (err) {
          console.error('[compilationService] ZIP download failed:', err)
          await markCompilationPartiallyDownloaded(
            compilationId,
            err instanceof Error ? err.message : 'ZIP download failed'
          )
        }
        onCompleted(compilationId)
        return
      }

      if (compilation.status === 'FAILED') {
        stopped = true
        onFailed?.(compilation.error ?? 'Compilation failed')
        return
      }

      if (compilation.status === 'CANCELLED') {
        stopped = true
        return
      }

      // Timeout
      if (Date.now() - startTime > MAX_POLL_TIME) {
        stopped = true
        await db.compilations.update(compilationId, {
          status: 'FAILED',
          error: 'Polling timeout after 10 minutes',
        })
        onFailed?.('Polling timeout after 10 minutes')
        return
      }

      timeoutHandle = setTimeout(poll, POLL_INTERVAL)
    } catch {
      // Erreur réseau : retry dans 5s
      if (!stopped) {
        timeoutHandle = setTimeout(poll, POLL_INTERVAL)
      }
    }
  }

  // Démarrer immédiatement
  poll()

  return () => {
    stopped = true
    if (timeoutHandle !== null) {
      clearTimeout(timeoutHandle)
    }
  }
}

// ============================================================================
// Téléchargement ZIP
// ============================================================================

interface ZipManifestJson {
  compilationId: string
  versionId: string
  totalLines: number
  totalDuration: number | null
  totalSize: number | null
  audioFormat: string
  audioBitrate: number
  lines: Array<{
    index: number
    lineIndex: number
    text: string
    characterName: string
    duration: number
    size: number
    voiceId: string
    contentHash: string
    fileHash: string
    cached: boolean
    filename: string
  }>
}

/**
 * Télécharge le ZIP d'une compilation terminée, dézippe côté client avec fflate,
 * vérifie les hashes SHA-256, et stocke les fichiers audio dans CacheStorage + IndexedDB.
 */
export async function downloadCompilationZip(compilationId: string): Promise<void> {
  // Si un téléchargement est déjà en cours pour ce compilationId, attendre qu'il se termine
  const inProgress = _zipDownloadInProgress.get(compilationId)
  if (inProgress) {
    return inProgress
  }

  // Tracker temps de démarrage pour monitoring stalled downloads
  _zipDownloadStartTimes.set(compilationId, Date.now())

  const promise = _doDownloadCompilationZip(compilationId)
  _zipDownloadInProgress.set(compilationId, promise)
  try {
    await promise
  } finally {
    _zipDownloadInProgress.delete(compilationId)
    _zipDownloadStartTimes.delete(compilationId)
  }
}

async function _doDownloadCompilationZip(compilationId: string): Promise<void> {
  const compilation = await db.compilations.get(compilationId)
  if (!compilation) throw new Error(`Compilation ${compilationId} not found locally`)

  const { API_BASE_URL, API_PREFIX } = await import('../config/api')
  const url = `${API_BASE_URL}${API_PREFIX}/audio/${compilationId}/download`

  let token = apiClient.getAccessToken()

  const DOWNLOAD_TIMEOUT_MS = 60000 // 60 secondes
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), DOWNLOAD_TIMEOUT_MS)

  let response: Response
  try {
    response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: controller.signal,
    })

    // Refresh automatique sur 401
    if (response.status === 401) {
      try {
        await apiClient.refreshAccessToken()
        token = apiClient.getAccessToken()
        response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        })
      } catch {
        // Refresh échoué → laisser le 401 remonter
      }
    }
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Téléchargement timeout après 60s - vérifiez votre connexion')
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }

  if (!response.ok) {
    throw new Error(`ZIP download HTTP ${response.status}: ${response.statusText}`)
  }

  const zipBuffer = await response.arrayBuffer()

  // Décompresser avec JSZip (déjà en dépendance)
  const JSZip = (await import('jszip')).default
  const zip = await JSZip.loadAsync(zipBuffer)

  // Lire manifest.json
  const manifestFile = zip.file('manifest.json')
  if (!manifestFile) throw new Error('manifest.json absent du ZIP')

  const manifestJson: ZipManifestJson = JSON.parse(await manifestFile.async('string'))

  const missingAudio: MissingAudio[] = []

  let cacheHits = 0
  let cacheMisses = 0
  let skippedNoHash = 0

  for (const line of manifestJson.lines) {
    if (!line.contentHash) {
      skippedNoHash++
      continue
    }

    // Déduplication : ne pas re-télécharger si déjà présent en CacheStorage
    // ET que le fileHash correspond (guard contre un audio régénéré côté serveur
    // dont le WAV a changé alors que le contentHash text|voiceId est identique).
    const alreadyCached = await db.audioCache.get(line.contentHash)
    if (alreadyCached) {
      const cache = await caches.open('repet-audio')
      const exists = await cache.match(new Request(alreadyCached.filePath))
      if (exists) {
        // Si le manifest fournit un fileHash, vérifier que le blob local est identique.
        // Un fileHash différent signifie que le serveur a régénéré l'audio (ex: après
        // correction de normalisation TTS) → forcer le remplacement.
        const cachedFileHash = exists.headers.get('X-File-Hash')
        if (!line.fileHash || !cachedFileHash || cachedFileHash === line.fileHash) {
          cacheHits++
          continue
        }
        console.warn(
          `[compilationService] fileHash mismatch pour ${line.contentHash.slice(0, 12)}…` +
            ` local=${cachedFileHash.slice(0, 12)}… serveur=${line.fileHash.slice(0, 12)}… — remplacement`
        )
      } else {
        // Entrée IndexedDB orpheline (fichier absent) → retélécharger
        console.warn(
          `[compilationService] audioCache orphelin pour hash ${line.contentHash.slice(0, 12)}… — retéléchargement`
        )
      }
    }
    cacheMisses++

    const zipEntry = zip.file(line.filename)
    if (!zipEntry) {
      console.warn(`[compilationService] Fichier absent du ZIP: ${line.filename}`)
      // Planifier téléchargement individuel si on avait une URL directe
      // (non disponible dans le flow ZIP — on le note comme partiellement téléchargé)
      missingAudio.push({
        hash: line.contentHash,
        url: '', // pas d'URL directe disponible dans ce flow
        text: line.text,
        voiceId: line.voiceId,
        duration: line.duration,
        size: line.size,
        versionId: manifestJson.versionId,
      })
      continue
    }

    const fileArrayBuffer = await zipEntry.async('arraybuffer')

    // Vérification d'intégrité : SHA-256 du contenu binaire doit correspondre
    // au hash stocké dans X-Content-Hash (ajouté par le backend dans les headers du ZIP).
    // Le contentHash du manifest est SHA256(text|voiceId) et sert à la dédup inter-versions ;
    // le fileHash est SHA256(octets WAV) et garantit l'intégrité du fichier reçu.
    const fileHashBuffer = await crypto.subtle.digest('SHA-256', fileArrayBuffer)
    const fileHashHex = Array.from(new Uint8Array(fileHashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')

    const expectedFileHash = line.fileHash // fourni par le backend dans le manifest
    if (expectedFileHash && fileHashHex !== expectedFileHash) {
      console.error(
        `[compilationService] Intégrité échouée pour ${line.filename}: ` +
          `attendu=${expectedFileHash}, reçu=${fileHashHex}`
      )
      missingAudio.push({
        hash: line.contentHash,
        url: '',
        text: line.text,
        voiceId: line.voiceId,
        duration: line.duration,
        size: line.size,
        versionId: manifestJson.versionId,
      })
      continue
    }

    const cacheKey = `https://localhost/audio/${line.contentHash}`
    const blob = new Blob([fileArrayBuffer], { type: 'audio/wav' })

    const cache = await caches.open('repet-audio')
    await cache.put(
      new Request(cacheKey),
      new Response(blob, {
        headers: {
          'Content-Type': 'audio/wav',
          'Content-Length': String(fileArrayBuffer.byteLength),
          'X-File-Hash': fileHashHex,
        },
      })
    )

    // Upsert dans IndexedDB : put() évite le ConstraintError en cas de reprise
    // (entrée déjà créée par un téléchargement précédent interrompu ou concurrent)
    await db.audioCache.put({
      id: alreadyCached?.id ?? generateId(),
      hash: line.contentHash,
      text: line.text,
      voiceId: line.voiceId,
      filePath: cacheKey,
      duration: line.duration,
      size: line.size,
      useCount: (alreadyCached?.useCount || 0) + 1,
      usageCount: (alreadyCached?.usageCount || 0) + 1,
      lastUsedAt: new Date().toISOString(),
      createdAt: alreadyCached?.createdAt ?? new Date().toISOString(),
    })
  }

  console.warn(
    `[compilationService] _doDownloadCompilationZip ${compilationId.slice(0, 8)}…` +
      ` — ${manifestJson.lines.length} lignes manifest,` +
      ` ${cacheHits} déjà en cache local, ${cacheMisses} téléchargés,` +
      ` ${skippedNoHash} sans hash, ${missingAudio.length} manquants/corrompus`
  )

  // Fichiers absents du ZIP ou corrompus → marquer PARTIALLY_DOWNLOADED
  // (pas d'URL directe disponible dans le flow ZIP, le retry complet est nécessaire)
  if (missingAudio.length > 0) {
    console.warn(
      `[compilationService] ${missingAudio.length} fichier(s) absent(s) ou corrompus dans le ZIP`
    )
    await markCompilationPartiallyDownloaded(
      compilationId,
      `${missingAudio.length} fichier(s) absent(s) ou avec hash invalide dans le ZIP`
    )
  }

  // Stocker le manifest localement dans IndexedDB (pour useAudioPlayback)
  const localManifest: AudioManifestLocal = {
    compilationId: manifestJson.compilationId,
    versionId: manifestJson.versionId,
    totalLines: manifestJson.totalLines,
    totalDuration: manifestJson.totalDuration,
    totalSize: manifestJson.totalSize,
    audioFormat: manifestJson.audioFormat,
    lines: manifestJson.lines.map(
      (l): ManifestLine => ({
        index: l.index,
        lineIndex: l.lineIndex,
        text: l.text,
        characterName: l.characterName,
        duration: l.duration,
        size: l.size,
        voiceId: l.voiceId,
        contentHash: l.contentHash,
        cached: l.cached,
        filename: l.filename,
      })
    ),
  }

  await storeAudioManifest(compilationId, localManifest)

  console.warn(
    `[compilationService] manifest stocké pour ${compilationId.slice(0, 8)}…` +
      ` — ${localManifest.lines.length} lignes,` +
      ` versionId=${localManifest.versionId?.slice(0, 8)}…`
  )
}

// ============================================================================
// cancelCompilation
// ============================================================================

export async function cancelCompilation(compilationId: string): Promise<void> {
  await apiClient.post(`/audio/${compilationId}/cancel`)
  await db.compilations.update(compilationId, { status: 'CANCELLED' })
}

// ============================================================================
// cleanupOrphanCompilations
// ============================================================================

/**
 * Détecte et corrige les compilations orphelines (COMPLETED sans audio local).
 * Appelée au boot de l'application pour nettoyer les états incohérents.
 *
 * Une compilation orpheline = status COMPLETED dans IndexedDB mais audio manquant
 * localement (CacheStorage ou manifest incomplet). Cela peut arriver si:
 * - Download ZIP interrompu avant la fin
 * - Erreur réseau silencieuse (avant Phase 1)
 * - Cache navigateur purgé manuellement
 */
export async function cleanupOrphanCompilations(): Promise<void> {
  console.warn('[compilationService] Starting orphan compilations cleanup...')

  try {
    // 1. Récupérer toutes les compilations COMPLETED
    const completed = await db.compilations.filter((c) => c.status === 'COMPLETED').toArray()

    console.warn(`[compilationService] Found ${completed.length} COMPLETED compilations`)

    let orphansFound = 0

    // 2. Valider présence audio pour chacune
    const { validateCompilationAudio } = await import('./audioValidationService')

    for (const compilation of completed) {
      const hasAudio = await validateCompilationAudio(compilation.id)

      if (!hasAudio) {
        orphansFound++
        console.warn(
          `[compilationService] Orphan detected: ${compilation.id.slice(0, 8)}… ` +
            `(version ${compilation.textVersionId.slice(0, 8)}…) - marking for retry`
        )

        // Marquer PARTIALLY_DOWNLOADED pour déclencher retry via sync
        await db.compilations.update(compilation.id, {
          status: 'PARTIALLY_DOWNLOADED',
          error: 'Audio missing locally - orphan detected at boot',
        })
      }
    }

    if (orphansFound === 0) {
      console.warn('[compilationService] No orphan compilations found')
      return
    }

    console.warn(`[compilationService] Found ${orphansFound} orphan(s), triggering downloads...`)

    // 3. Déclencher download pour les orphelins (non-bloquant)
    const needsDownload = await db.compilations
      .filter((c) => c.status === 'PARTIALLY_DOWNLOADED' && (c.error?.includes('orphan') ?? false))
      .toArray()

    for (const compilation of needsDownload) {
      downloadCompilationZip(compilation.id)
        .then(async () => {
          await db.compilations.update(compilation.id, {
            status: 'COMPLETED',
          })
          console.warn(
            `[compilationService] Orphan ${compilation.id.slice(0, 8)}… recovered successfully`
          )
        })
        .catch(async (err) => {
          console.error(
            `[compilationService] Orphan ${compilation.id.slice(0, 8)}… recovery failed:`,
            err
          )
          await db.compilations.update(compilation.id, {
            status: 'PARTIALLY_DOWNLOADED',
            error: `Retry download failed: ${err.message}`,
          })
        })
    }

    console.warn('[compilationService] Orphan cleanup initiated')
  } catch (err) {
    console.error('[compilationService] Cleanup orphans failed:', err)
    // Ne pas bloquer l'application en cas d'erreur
  }
}

// ============================================================================
// getCompilationsForVersion
// ============================================================================

export async function getCompilationsForVersion(versionId: string): Promise<Compilation[]> {
  return db.compilations.where('textVersionId').equals(versionId).reverse().sortBy('createdAt')
}

export async function getLatestCompilationForVersion(
  versionId: string
): Promise<Compilation | undefined> {
  const all = await getCompilationsForVersion(versionId)
  return all[0]
}

// ============================================================================
// Download Monitoring
// ============================================================================

/**
 * Nettoie les téléchargements bloqués (stalled) qui dépassent le seuil de timeout.
 * Appelée périodiquement (toutes les 30s) pour libérer les verrous zombies.
 */
function _cleanupStalledDownloads(): void {
  const now = Date.now()
  const stalledIds: string[] = []

  for (const [compilationId, startTime] of _zipDownloadStartTimes.entries()) {
    const elapsed = now - startTime

    if (elapsed > STALLED_DOWNLOAD_THRESHOLD_MS) {
      stalledIds.push(compilationId)
      console.error(
        `[compilationService] STALLED download detected: ${compilationId.slice(0, 8)}…, ` +
          `elapsed ${Math.round(elapsed / 1000)}s - forcing cleanup`
      )
    }
  }

  // Nettoyer les verrous
  for (const compilationId of stalledIds) {
    _zipDownloadInProgress.delete(compilationId)
    _zipDownloadStartTimes.delete(compilationId)

    // Marquer en DB comme partiellement téléchargé
    db.compilations
      .update(compilationId, {
        status: 'PARTIALLY_DOWNLOADED',
        error: 'Téléchargement timeout (connexion trop lente ou interrompue)',
      })
      .catch((err) => {
        console.error(
          `[compilationService] Failed to mark ${compilationId} as PARTIALLY_DOWNLOADED:`,
          err
        )
      })
  }

  if (stalledIds.length > 0) {
    console.warn(`[compilationService] Cleaned up ${stalledIds.length} stalled download(s)`)
  }
}

/**
 * Démarre le monitoring des téléchargements bloqués.
 * Appelée au boot de l'application.
 */
export function startDownloadMonitoring(): void {
  if (_cleanupIntervalId) {
    console.warn('[compilationService] Download monitoring already started')
    return
  }

  console.warn('[compilationService] Starting download monitoring (cleanup every 30s)')

  _cleanupIntervalId = setInterval(() => {
    _cleanupStalledDownloads()
  }, CLEANUP_INTERVAL_MS)
}

/**
 * Arrête le monitoring (utile pour tests ou cleanup app).
 */
export function stopDownloadMonitoring(): void {
  if (_cleanupIntervalId) {
    clearInterval(_cleanupIntervalId)
    _cleanupIntervalId = null
    console.warn('[compilationService] Download monitoring stopped')
  }
}

/**
 * Force le retry d'un téléchargement bloqué.
 * Nettoie le verrou en mémoire et relance le download.
 * Utilisé par l'UI quand l'utilisateur clique sur "Réessayer" après détection de stall.
 */
export async function forceRetryDownload(compilationId: string): Promise<void> {
  console.warn(`[compilationService] Force retry download for ${compilationId.slice(0, 8)}…`)

  // 1. Supprimer le verrou (libérer la Promise bloquée)
  _zipDownloadInProgress.delete(compilationId)
  _zipDownloadStartTimes.delete(compilationId)

  // 2. Relancer le download
  return downloadCompilationZip(compilationId)
}
